import { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { useInteractMedia } from 'Hooks/Document';
import { throttle } from 'lodash-es';

interface UseFieldShapeParams {
  isFieldShapeExist: boolean;
  onRemoveFieldShape: () => void;
}

const invalidClassName = 'invalid';

export default ({ isFieldShapeExist, onRemoveFieldShape }: UseFieldShapeParams) => {
  const fieldShapeContainerRef = useRef<HTMLDivElement | null>(null);
  const fieldRootRef = useRef<ReactDOM.Root | null>(null);
  const [isMobile] = useInteractMedia(['sm']);

  const addInvalidityToFieldShape = useCallback(() => {
    fieldShapeContainerRef.current?.classList.add(invalidClassName);
  }, []);

  const removeInvalidityFromFieldShape = useCallback(() => {
    fieldShapeContainerRef.current?.classList.remove(invalidClassName);
  }, []);

  const removeFieldShape = useCallback(() => {
    if (
      fieldRootRef.current &&
      fieldShapeContainerRef.current &&
      fieldShapeContainerRef.current.classList.contains(invalidClassName) &&
      isFieldShapeExist &&
      !isMobile
    ) {
      fieldRootRef.current.unmount();
      onRemoveFieldShape();
    }
  }, [isFieldShapeExist, isMobile, onRemoveFieldShape]);

  const mouseMoveWithFieldShape = useCallback(e => {
    if (fieldShapeContainerRef.current) {
      fieldShapeContainerRef.current.style.top = `${e.y}px`;
      fieldShapeContainerRef.current.style.left = `${e.x}px`;
    }
  }, []);

  useEffect(() => {
    const callback = throttle(mouseMoveWithFieldShape, 20);

    if (fieldShapeContainerRef.current && isFieldShapeExist) {
      window.addEventListener('mousemove', callback);
      window.addEventListener('click', removeFieldShape);
    }

    return () => {
      window.removeEventListener('mousemove', callback);
      window.removeEventListener('click', removeFieldShape);
    };
  }, [isFieldShapeExist, mouseMoveWithFieldShape, removeFieldShape]);

  return {
    fieldRootRef,
    fieldShapeContainerRef,
    removeFieldShape,
    addInvalidityToFieldShape,
    removeInvalidityFromFieldShape,
  };
};
